import { ComponentType, useState } from 'react';
import { Box, Button, Stack, Skeleton } from '@mui/material';
import { TableView } from '../../components/table-view';
import { RadioGroup } from '../../components/fields/radio-group';
import { PageHeader } from '../../components/page-header';
import { WeekRangePicker } from '../../components/week-range-picker/WeekRangePicker';
import { CalendarView } from '../../components/calendar-view/CalendarView';
import { AppointmentCalendarCard } from '../../components/appointment-calendar-card';
import { AppointmentCalendarDrawerCard } from '../../components/appointment-calendar-drawer-card';
import { useParams } from 'react-router-dom';
import { AppointmentDto } from '../../core/http';
import { get } from 'lodash';
import { useClientDetail } from '../../core/hooks/useClientDetail';
import {
  calendarStatusFilterItems,
  calendarViewTypeItems
} from '../../constants/app.constants';
import { useAppointmentList } from '../../core/hooks/useAppointmentList';
import { AppointmentActionButtons } from '../../components/appointment-action-buttons/appointment-action-buttons';
import { clientAppointmentColumns } from '../../constants/columns';
import { AppointmentDrawerActions } from '../../components/appointment-drawer-actions';
import { PermissionView } from '../../components/permission-view';
import { AddNewAppointmentDialog } from './dialogs/AddNewAppointmentDialog';
import { useDialog } from '../../core/hooks/useDialog';

export const AppointmentsList: ComponentType = () => {
  const { id } = useParams();
  const [currentView, setCurrentView] = useState<'calendar' | 'table'>(
    'calendar'
  );
  const [openAddAppointment] = useDialog(AddNewAppointmentDialog);
  const { data: clientData, isLoading: clientIsLoading } = useClientDetail({
    uuid: id
  });
  const {
    data: appointmentResponse,
    isLoading,
    pagination,
    handleChangePerPage,
    handleChangePage,
    handleChangeSort,
    handleStatusFilter,
    handleCancel,
    handleDelete,
    handleConvert,
    handleClientView: handleView,
    handleClientEdit: handleEdit,
    handleClientMakeUp: handleMakeUp,
    startDate,
    setStartDate
  } = useAppointmentList({
    clientId: id,
    defaultParams: {
      'filter[client.uuid][eq]': id,
      limit: 20
    }
  });

  const data = get(appointmentResponse, 'items', []);
  const isDisabled = clientData?.status === 'terminated';
  const headerProps = {
    divider: false,
    title:
      clientIsLoading || !data ? (
        <Stack direction="row" spacing={2}>
          <Skeleton variant="text" sx={{ height: 32, width: 120 }} />
          <Skeleton variant="text" sx={{ height: 32, width: 120 }} />
        </Stack>
      ) : (
        clientData?.displayName + '’ appointments'
      )
  };
  return (
    <>
      <PageHeader {...headerProps}>
        <Button color="secondary">Export</Button>
      </PageHeader>
      <Stack direction="row" justifyContent="space-between" mb={3} mt={3}>
        <Stack direction="row" spacing={3}>
          <PermissionView permission="modifyAppointments">
            <Button
              disabled={isDisabled}
              onClick={() => openAddAppointment({ clientUuid: id })}
            >
              Add new
            </Button>
          </PermissionView>
          <RadioGroup
            items={calendarViewTypeItems}
            defaultValue="calendar"
            onChange={(view) => {
              setCurrentView(view as 'calendar' | 'table');
            }}
          />
          <RadioGroup
            items={calendarStatusFilterItems}
            defaultValue="all"
            onChange={handleStatusFilter}
          />
        </Stack>
        <WeekRangePicker
          withToday
          onChange={(startDate) => {
            setStartDate(startDate);
          }}
        />
      </Stack>
      <Box>
        {currentView === 'calendar' ? (
          <CalendarView
            startDate={startDate}
            dataSource={data}
            groupKey="startDate"
            renderItem={(item: AppointmentDto, _, selectedItem) => (
              <AppointmentCalendarCard
                item={item}
                selected={selectedItem?.uuid === item.uuid}
              />
            )}
            renderDrawer={(item: AppointmentDto) => (
              <AppointmentCalendarDrawerCard item={item} />
            )}
            renderDrawerFooter={(appointment: AppointmentDto) => (
              <AppointmentDrawerActions
                {...{
                  appointment,
                  handleConvert,
                  handleDelete,
                  handleEdit,
                  handleCancel,
                  handleView,
                  handleMakeUp
                }}
              />
            )}
          />
        ) : (
          <TableView
            showEmptyState
            showTablePerPage
            showPagination
            loading={isLoading}
            onChangePage={handleChangePage}
            pagination={pagination}
            onChangePerPage={handleChangePerPage}
            onChangeSort={handleChangeSort}
            EmptyStateProps={{
              title: 'Appointments has not been found'
            }}
            dataSource={data}
            keyExtractor={(item) => item.uuid}
            columns={[
              ...clientAppointmentColumns,
              {
                title: '',
                dataIndex: 'actions',
                key: 'actions',
                render: (_, appointment: AppointmentDto) => (
                  <AppointmentActionButtons
                    {...{
                      appointment,
                      handleConvert,
                      handleDelete,
                      handleEdit,
                      handleCancel,
                      handleView,
                      handleMakeUp
                    }}
                  />
                )
              }
            ]}
          />
        )}
      </Box>
    </>
  );
};
